import React, { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Star, Heart, Award, Users, Play, Eye } from "lucide-react";

const Hero = ({ setActiveTab }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  // Gallery items for homepage preview
  const featuredGalleryItems = [
    { 
      id: 1, 
      type: "image", 
      src: "/images/006a936b-7913-43f6-bd9c-7b076a796eb4.jpg"
    },
    { 
      id: 2, 
      type: "image", 
      src: "/images/04cb29bf-607a-4a95-80c3-ce90d2647c14.jpg"
    },
    { 
      id: 6, 
      type: "image", 
      src: "/images/2cb5f0a3-e10c-40da-8a15-5ec4f78f47df.jpg"
    },
    { 
      id: 9, 
      type: "image", 
      src: "/images/5cb7ab07-ff16-4e75-a4a9-148a3daef6e6.jpg"
    },
    { 
      id: 14, 
      type: "image", 
      src: "/images/d5d4aedb-701e-4b7c-9dfd-5691c5c237ed.jpg"
    },
    { 
      id: 16, 
      type: "image", 
      src: "/images/f4c6b5c9-1bb4-4df1-ab3d-c71bde97cc59.jpg"
    },
    { 
      id: 17, 
      type: "video", 
      src: "/images/WhatsApp Video 2025-07-07 at 5.14.47 PM.mp4"
    },
    { 
      id: 18, 
      type: "video", 
      src: "/images/WhatsApp Video 2025-07-07 at 5.17.13 PM.mp4"
    }
  ];
  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1640347852794-814af9f94d48')`,
          }}
        >
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/30 to-orange-900/30"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="animate-fade-in-up">
              <Badge className="mb-6 bg-amber-500/20 text-amber-100 border-amber-300 px-4 py-2 text-sm font-medium">
                Professional Southern Chef
              </Badge>
              
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                Chef <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Aaron</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
                From Southern soul food to gourmet fusion dishes, bringing generations of culinary tradition to your table
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <a
                  href="https://api.whatsapp.com/send/?phone=14242830606&text=Hi%20Chef%20Aaron,%20I%20would%20like%20to%20discuss%20booking%20your%20culinary%20services!&type=phone_number&app_absent=0"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-4 text-lg font-medium rounded-full shadow-2xl hover:scale-105 transition-all duration-300"
                  >
                    <Heart className="w-5 h-5 mr-2" />
                    Book Chef Aaron
                  </Button>
                </a>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-black hover:bg-white hover:text-gray-900 px-8 py-4 text-lg font-medium rounded-full backdrop-blur-sm hover:scale-105 transition-all duration-300 bg-white/90"
                  onClick={() => setActiveTab && setActiveTab("gallery")}
                >
                  <Award className="w-5 h-5 mr-2" />
                  View Gallery
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white/95 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                A Journey Through <span className="text-amber-600">Southern Heritage</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto mb-8"></div>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-8">
                  <div className="flex items-center mb-6">
                    <div className="bg-amber-500 p-3 rounded-full mr-4">
                      <Heart className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Southern Roots</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Aaron was raised by a southern mother from Mississippi who always cooked soul food and fusion dishes. 
                    Going to his grandmother's for Thanksgiving was the greatest time. These delicious dishes he learned to make after requesting recipes in college.
                  </p>
                  <div className="flex items-center text-amber-600">
                    <Star className="w-5 h-5 mr-2" />
                    <span className="font-semibold">Generations of Tradition</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-8">
                  <div className="flex items-center mb-6">
                    <div className="bg-orange-500 p-3 rounded-full mr-4">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">College Kitchen</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    As a basketball player in college he had to learn to cook. His teammates fell in love with his cooking which led him to learn more and have motivation to develop his incredible culinary skills.
                  </p>
                  <div className="flex items-center text-orange-600">
                    <Award className="w-5 h-5 mr-2" />
                    <span className="font-semibold">Passion Ignited</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="mt-12 bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-2xl">
              <CardContent className="p-8 text-center">
                <h3 className="text-3xl font-bold mb-4">Master of Culinary Arts</h3>
                <p className="text-xl mb-6 text-amber-100">
                  Aaron's skill level at cooking allows him to make even 10-course meals such as holiday feasts! 
                  From intimate dinners to grand celebrations, Chef Aaron brings Southern hospitality and gourmet excellence to every dish.
                </p>
                <div className="flex justify-center items-center space-x-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold">10+</div>
                    <div className="text-amber-200">Course Meals</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Gallery Preview Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Featured <span className="text-amber-600">Culinary Creations</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Here are pictures of his delicious creations. Experience the artistry and passion that goes into every dish. From elegant presentations to mouth-watering flavors.
            </p>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {featuredGalleryItems.map((item, index) => (
              <div
                key={item.id}
                className="group relative cursor-pointer transform transition-all duration-500 hover:-translate-y-2"
                style={{ animationDelay: `${index * 100}ms` }}
                onClick={() => setSelectedImage(item)}
              >
                <div className="relative bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500">
                  <div className="relative aspect-square overflow-hidden">
                    {item.type === "image" ? (
                      <img
                        src={item.src}
                        alt="Gallery preview"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                      />
                    ) : (
                      <div className="relative w-full h-full">
                        <video
                          src={item.src}
                          className="w-full h-full object-cover"
                          muted
                          preload="metadata"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex items-center justify-center">
                          <div className="bg-white/95 backdrop-blur-sm rounded-full p-3 group-hover:scale-110 transition-transform duration-300 shadow-2xl">
                            <Play className="w-6 h-6 text-amber-600" fill="currentColor" />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                        <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-xl">
                          <Eye className="w-5 h-5 text-amber-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* View More Button */}
          <div className="text-center">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-4 text-lg font-medium rounded-full shadow-xl hover:scale-105 transition-all duration-300"
              onClick={() => setActiveTab && setActiveTab("gallery")}
            >
              <Eye className="w-5 h-5 mr-2" />
              View Full Gallery
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-gray-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready for an <span className="text-amber-400">Amazing</span> Culinary Experience?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Aaron loves to cook and can create something amazing for you, your friends, family, and parties. 
            Let's make your next meal unforgettable!
          </p>
          <Button 
            size="lg" 
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-10 py-4 text-lg font-medium rounded-full shadow-2xl hover:scale-105 transition-all duration-300"
          >
            Contact Chef Aaron Today
          </Button>
        </div>
      </section>

      {/* Gallery Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-2 sm:p-4 backdrop-blur-sm">
          {/* Close Button */}
          <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
            <Button
              onClick={() => setSelectedImage(null)}
              variant="ghost"
              className="p-2 sm:p-3 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-all duration-200 text-white hover:text-white"
            >
              ✕
            </Button>
          </div>
          
          {/* Media Content */}
          <div className="max-w-full max-h-full w-full flex items-center justify-center px-2 sm:px-4">
            {selectedImage.type === "image" ? (
              <img
                src={selectedImage.src}
                alt="Gallery image"
                className="max-w-full max-h-[85vh] sm:max-h-[90vh] object-contain rounded-lg sm:rounded-xl shadow-2xl"
              />
            ) : (
              <video
                src={selectedImage.src}
                controls
                autoPlay
                className="max-w-full max-h-[85vh] sm:max-h-[90vh] object-contain rounded-lg sm:rounded-xl shadow-2xl"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Hero;